import React, {
  ComponentPropsWithRef,
  useCallback,
  useEffect,
  useState
} from 'react'
import { EmblaCarouselType } from 'embla-carousel'

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean
  nextBtnDisabled: boolean
  onPrevButtonClick: () => void
  onNextButtonClick: () => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (emblaApi: EmblaCarouselType) => void
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
    if (onButtonClick) onButtonClick(emblaApi)
  }, [emblaApi, onButtonClick])

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
    if (onButtonClick) onButtonClick(emblaApi)
  }, [emblaApi, onButtonClick])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  }
}

export type ArrowType = 'filled' | 'outline' | 'chevronOnly'

type ButtonProps = ComponentPropsWithRef<'button'> & {
  elementColor?: string
  arrowType?: ArrowType
}

const arrowTypeConfig: Record<ArrowType, {
  className: string
  getStyle: (color: string) => React.CSSProperties
}> = {
  outline: {
    className: '',
    getStyle: (color) => ({ color })
  },
  filled: {
    className: 'embla__button--filled',
    getStyle: (color) => ({ color: '#ffffff', backgroundColor: color, borderColor: color })
  },
  chevronOnly: {
    className: 'embla__button--chevron-only',
    getStyle: (color) => ({ color, border: 'none', backgroundColor: 'transparent' })
  }
}

export const PrevButton: React.FC<ButtonProps> = ({ elementColor, arrowType = 'outline', children, className = '', ...restProps }) => {
  const config = arrowTypeConfig[arrowType]

  return (
    <button
      aria-label='Anterior'
      className={`embla__button embla__button--prev ${config.className} ${className}`.trim()}
      type="button"
      style={config.getStyle(elementColor || '#334155')}
      {...restProps}
    >
      <svg className="embla__button__svg" viewBox="0 0 532 532">
        <path
          fill="currentColor"
          d="M355.66 11.354c13.793-13.805 36.208-13.805 50.001 0 13.785 13.804 13.785 36.238 0 50.034L201.22 266l204.442 204.61c13.785 13.805 13.785 36.239 0 50.044-13.793 13.796-36.208 13.796-50.002 0a5994246.277 5994246.277 0 0 0-229.332-229.454 35.065 35.065 0 0 1-10.326-25.126c0-9.2 3.393-18.26 10.326-25.2C172.192 194.973 332.731 34.31 355.66 11.354Z"
        />
      </svg>
      {children}
    </button>
  )
}

export const NextButton: React.FC<ButtonProps> = ({ elementColor, arrowType = 'outline', children, className = '', ...restProps }) => {
  const config = arrowTypeConfig[arrowType]

  return (
    <button
      aria-label='Próximo'
      className={`embla__button embla__button--next ${config.className} ${className}`.trim()}
      type="button"
      style={config.getStyle(elementColor || '#334155')}
      {...restProps}
    >
      <svg className="embla__button__svg" viewBox="0 0 532 532">
        <path
          fill="currentColor"
          d="M176.34 520.646c-13.793 13.805-36.208 13.805-50.001 0-13.785-13.804-13.785-36.238 0-50.034L330.78 266 126.34 61.391c-13.785-13.805-13.785-36.239 0-50.044 13.793-13.796 36.208-13.796 50.002 0 22.928 22.947 206.395 206.507 229.332 229.454a35.065 35.065 0 0 1 10.326 25.126c0 9.2-3.393 18.26-10.326 25.2-45.865 45.901-206.404 206.564-229.332 229.52Z"
        />
      </svg>
      {children}
    </button>
  )
}

type ProgressBarProps = {
  scrollProgress?: number
  elementColor?: string
  barRef?: React.RefObject<HTMLDivElement>
}

export const ProgressBar = ({ scrollProgress = 0, elementColor, barRef }: ProgressBarProps) => {
  return (
    <div className='embla__progress' style={{ backgroundColor: !elementColor ? '#33415533' : `${elementColor}33` }}>
      <div
        ref={barRef}
        className='embla__progress__bar'
        style={{ transform: `translate3d(${scrollProgress}%,0px,0px)`, backgroundColor: elementColor || '#334155' }}
      />
    </div>
  )
}

type UseDotButtonType = {
  selectedIndex: number
  scrollSnaps: number[]
  onDotButtonClick: (index: number) => void
}

// eslint-disable-next-line react-refresh/only-export-components
export const useDotButton = (
  emblaApi: EmblaCarouselType | undefined
): UseDotButtonType => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  const onDotButtonClick = useCallback(
    (index: number) => {
      if (!emblaApi) return
      emblaApi.scrollTo(index)
    },
    [emblaApi]
  )

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    setScrollSnaps(emblaApi.scrollSnapList())
  }, [])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    onSelect(emblaApi)
    emblaApi.on('reInit', onInit).on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onInit, onSelect])

  return {
    selectedIndex,
    scrollSnaps,
    onDotButtonClick
  }
}

type PropType = ComponentPropsWithRef<'button'> & {
  elementColor?: string
}

export const DotButton: React.FC<PropType> = ({ elementColor, children, ...restProps }) => {
  return (
    <button
      type="button"
      style={{ border: `2px solid ${elementColor || '#334155'}` }}
      {...restProps}
    >
      {children}
    </button>
  )
}
